/**
 * Otomatik kampanya hediye ürünü sepete ekleme/çıkarma kontrolü.
 * api-campaign-calculate section'ından gelen verilere göre hediye ürününü yönetir.
 *
 * @class HPAutoCampaignControl
 */
class HPAutoCampaignControl {
  /** @type {string} */
  static SECTION_ID = 'api-campaign-calculate';

  /** @type {string} */
  static DATA_ELEMENT_ID = 'api-campaign-calculate-data';

  /** @type {string} */
  static EVENT_NAME = 'cart:campaignCalculate';

  /** @type {boolean} Bir hediye mutasyonu (ekle/çıkar/azalt) sürüyor mu. */
  static _giftMutationInFlight = false;

  /** @type {boolean} Mutasyon sırasında yeni bir değerlendirme talebi geldi mi. */
  static _giftRecheckPending = false;

  /** @type {Object|null} Son geçerli kampanya verisi (flicker'sız re-paint için). */
  static _lastData = null;

  /**
   * api-campaign-calculate section HTML'inden JSON verisini parse eder.
   *
   * @param {string} sectionHtml - response.sections['api-campaign-calculate'] HTML string
   * @returns {Object|null} Parse edilmiş campaign verisi veya null
   */
  static parseCampaignCalculateData(sectionHtml) {
    if (!sectionHtml || typeof sectionHtml !== 'string') return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const scriptEl = doc.getElementById(HPAutoCampaignControl.DATA_ELEMENT_ID);
      if (!scriptEl?.textContent) return null;
      return JSON.parse(scriptEl.textContent.trim());
    } catch (e) {
      console.warn('hp-auto-campaign-control: Failed to parse campaign calculate data', e);
      return null;
    }
  }

  /**
   * Section Rendering API ile api-campaign-calculate section'ını cart context'inde çeker.
   *
   * @returns {Promise<string|null>} Sayfa HTML (section içerir) veya null
   */
  static async fetchSectionHtml() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const url = `${cartUrl}?section_id=${HPAutoCampaignControl.SECTION_ID}`;
    try {
      const res = await fetch(url);
      return res.ok ? res.text() : null;
    } catch (e) {
      console.warn('hp-auto-campaign-control: Failed to fetch section', e);
      return null;
    }
  }

  /**
   * Section HTML'den campaign verisini alır. Önce response'tan, yoksa Section Rendering API ile.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   * @returns {Promise<Object|null>} Campaign verisi veya null
   */
  static async getCampaignDataFromResponse(response) {
    let sectionHtml = response?.sections?.[HPAutoCampaignControl.SECTION_ID];

    if (!sectionHtml) {
      sectionHtml = await HPAutoCampaignControl.fetchSectionHtml();
    }

    return sectionHtml ? HPAutoCampaignControl.parseCampaignCalculateData(sectionHtml) : null;
  }

  /**
   * Cart API response'unda api-campaign-calculate varsa event tetikler.
   * Yoksa Section Rendering API ile section çekip event tetikler.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   */
  static async dispatchCampaignCalculateIfNeeded(response) {
    const campaignData = await HPAutoCampaignControl.getCampaignDataFromResponse(response);
    if (!campaignData) return;

    document.dispatchEvent(
      new CustomEvent(HPAutoCampaignControl.EVENT_NAME, { detail: campaignData })
    );
  }

  /**
   * Kampanya hediye ürününü sepete ekler.
   *
   * @param {number} giftVariantId - Hediye ürünü variant ID
   * @returns {Promise<void>}
   */
  static async addGiftToCart(giftVariantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id: giftVariantId, quantity: 1 }],
      }),
    });

    // Stok tükenmesi gibi durumlarda /cart/add.js 422 döner; ürün eklenmez.
    // Pop-up YALNIZCA ürün gerçekten eklendiğinde (res.ok) gösterilir; aksi halde
    // "hediye kazandınız" yanılgısı oluşmaz.
    if (!res.ok) return;

    document.dispatchEvent(new CustomEvent('cart:updated'));
    HPAutoCampaignControl.showGiftPopup();
  }

  /**
   * Hediye barem ilerleme çubuğunu (drawer + cart) günceller.
   * Stok yok / kampanya kapalı / barem tanımsız ise çubuk tamamen gizlenir.
   *
   * @param {Object} data - api-campaign-calculate verisi
   */
  static updateGiftBar(data) {
    // Son geçerli durumu önbelleğe al; re-render sonrası anında (flicker'sız)
    // yeniden boyamak için kullanılır.
    if (data && typeof data === 'object') HPAutoCampaignControl._lastData = data;

    const bars = document.querySelectorAll('[data-campaign-gift-bar]');
    if (!bars.length) return;

    const hide = () => bars.forEach((bar) => bar.classList.add('hidden'));

    const campaignOff = typeof campaignActive !== 'undefined' && campaignActive !== 'true';
    if (campaignOff || !data || !data.gift_available || data.threshold == null) {
      hide();
      return;
    }

    const currencyCode =
      (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
    const symbol = currencyCode === 'TRY' ? 'TL' : currencyCode;

    let message;
    let percentage;

    if (data.eligible) {
      message = typeof campaignSuccessMessage !== 'undefined' ? campaignSuccessMessage : '';
      percentage = 100;
    } else {
      const remaining = (data.remaining / 100).toFixed(2);
      const priceLabel = `${remaining} ${symbol}`.trim();
      message =
        typeof campaignProgressMessage !== 'undefined'
          ? campaignProgressMessage.replace('||price||', priceLabel)
          : priceLabel;
      percentage =
        data.threshold > 0 ? Math.trunc((data.progress_total * 100) / data.threshold) : 0;
      if (percentage > 100) percentage = 100;
      if (percentage < 0) percentage = 0;
    }

    bars.forEach((bar) => {
      const msg = bar.querySelector('[data-campaign-gift-message]');
      const progress = bar.querySelector('.progress');
      if (!msg || !progress) return;
      // Gerçek içerik basıldı: gizli/yüklenme (skeleton) durumlarını kaldır.
      bar.classList.remove('hidden');
      bar.classList.remove('is-loading');
      msg.textContent = message;
      progress.style.setProperty('--progress', `${percentage}%`);
    });
  }

  /**
   * SADECE ilerleme çubuğunu yeniden boyar — sepete ekleme/çıkarma KARARI vermez.
   * Re-render sonrası (cart:updated, drawer açılışı vb.) çağrılır. Mutasyon
   * tetiklemediği için re-render → mutasyon → cart:updated zincirini (kararsızlık)
   * önler. Ekleme/çıkarma kararı yalnızca gerçek sepet işlemlerinde
   * (dispatchCampaignCalculateIfNeeded) verilir.
   *
   * @returns {Promise<void>}
   */
  static async refreshGiftBarOnly() {
    // Önce son bilinen durumla anında boya (re-render'da çubuğun bir an
    // gizli/boş görünüp "flicker" yapmasını önler), sonra güncel veriyle tazele.
    if (HPAutoCampaignControl._lastData) {
      HPAutoCampaignControl.updateGiftBar(HPAutoCampaignControl._lastData);
    }
    const data = await HPAutoCampaignControl.getCampaignDataFromResponse({});
    if (data) HPAutoCampaignControl.updateGiftBar(data);
  }

  /**
   * "Tebrikler, hediye ürün kazandınız." pop-up'ını gösterir.
   */
  static showGiftPopup() {
    const popup = document.querySelector('[data-campaign-gift-popup]');
    if (!popup) return;

    popup.classList.add('is-visible');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('campaign-gift-popup-open');

    if (!popup.dataset.bound) {
      popup.dataset.bound = 'true';
      popup.querySelectorAll('[data-campaign-gift-popup-close]').forEach((el) =>
        el.addEventListener('click', () => HPAutoCampaignControl.hideGiftPopup())
      );
    }

    clearTimeout(HPAutoCampaignControl._popupTimer);
    HPAutoCampaignControl._popupTimer = setTimeout(
      () => HPAutoCampaignControl.hideGiftPopup(),
      5000
    );
  }

  /**
   * Hediye pop-up'ını kapatır.
   */
  static hideGiftPopup() {
    const popup = document.querySelector('[data-campaign-gift-popup]');
    if (!popup) return;

    popup.classList.remove('is-visible');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('campaign-gift-popup-open');
    clearTimeout(HPAutoCampaignControl._popupTimer);
  }

  /**
   * Hediye satır(lar)ını TEK atomik `/cart/update.js` çağrısıyla düzenler.
   * Hediye variant'ı birden fazla satıra dağılmış olabileceği için (tek satır
   * qty>1 VEYA harici indirim/cart-transform'un böldüğü çoklu satır) bütün
   * hediye line item key'leri tek seferde set edilir:
   *   - keepOne = false → tüm hediye satırları 0 (tamamen kaldır)
   *   - keepOne = true  → ilk satır 1, diğer tüm hediye satırları 0 (tek 1 adet)
   * Line item key kullanıldığından pozisyon bağımsızdır; yanlış satır silinmez.
   *
   * @param {string[]} giftKeys - Tüm hediye satırlarının line item key'leri
   * @param {boolean} keepOne - true: tek adet bırak, false: hepsini sil
   * @returns {Promise<void>}
   */
  static async setGiftLines(giftKeys, keepOne) {
    if (!Array.isArray(giftKeys) || giftKeys.length === 0) return;
    const updates = {};
    giftKeys.forEach((key, index) => {
      updates[key] = keepOne && index === 0 ? 1 : 0;
    });
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ updates }),
    });
    // Yeniden değerlendirme settle-döngüsü tarafından yapılır; burada yalnızca
    // sepet görünümünü tazelemek için cart:updated tetiklenir.
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * cart:campaignCalculate event'ini işler ve gerekli sepete ekleme/çıkarma işlemini yapar.
   *
   * @param {CustomEvent} event - cart:campaignCalculate event
   */
  static handleCampaignCalculate(event) {
    // İlerleme çubuğu her durumda güncellenir (stok yoksa/kampanya kapalıysa gizlenir).
    HPAutoCampaignControl.updateGiftBar(event.detail);

    const { gift_variant_id, should_add, should_remove, should_reduce, gift_keys } =
      event.detail;

    // gift_variant_id null iken (kampanya pasif — örn. platform 'app'e çekildi)
    // ekleme yapılmaz; ama sepette kalmış eski hediye satırı varsa silme yolu
    // yine de çalışmalıdır (should_remove + gift_keys).
    const hasGiftLines = Array.isArray(gift_keys) && gift_keys.length > 0;
    const needsMutation =
      (should_add && gift_variant_id) || ((should_remove || should_reduce) && hasGiftLines);
    if (!needsMutation) return;

    // Aynı anda yalnızca bir settle-döngüsü çalışır. Döngü sürerken gelen yeni
    // kararlar işaretlenir ve döngü bitiminde bir kez daha değerlendirilir.
    if (HPAutoCampaignControl._giftMutationInFlight) {
      HPAutoCampaignControl._giftRecheckPending = true;
      return;
    }
    HPAutoCampaignControl._giftMutationInFlight = true;

    HPAutoCampaignControl.runGiftSettleLoop()
      .catch((err) =>
        console.error('hp-auto-campaign-control: gift settle loop failed', err)
      )
      .finally(() => {
        HPAutoCampaignControl._giftMutationInFlight = false;
        if (HPAutoCampaignControl._giftRecheckPending) {
          HPAutoCampaignControl._giftRecheckPending = false;
          HPAutoCampaignControl.dispatchCampaignCalculateIfNeeded({});
        }
      });
  }

  /**
   * Sepet kararlı (mutasyon gerekmeyen) hale gelene kadar hediye ürününü
   * ekler/çıkarır/azaltır. HER adımda GÜNCEL sepet durumunu yeniden okur; bu
   * sayede eski (stale) bir snapshot'a göre yanlış/çift ekleme yapılmaz.
   * Kilit (`_giftMutationInFlight`) tüm döngü boyunca tutulur.
   *
   * @returns {Promise<void>}
   */
  static async runGiftSettleLoop() {
    for (let i = 0; i < 5; i++) {
      const data = await HPAutoCampaignControl.getCampaignDataFromResponse({});
      HPAutoCampaignControl.updateGiftBar(data);
      if (!data) return;

      const { gift_variant_id, should_add, should_remove, should_reduce, gift_keys } = data;
      // gift_variant_id null (kampanya pasif) → ekleme yapılmaz; sepette kalmış
      // eski hediye satırı varsa yalnızca silme yolu çalışır.
      const hasGiftLines = Array.isArray(gift_keys) && gift_keys.length > 0;
      const needsMutation =
        (should_add && gift_variant_id) || ((should_remove || should_reduce) && hasGiftLines);
      if (!needsMutation) return; // kararlı

      if (should_add && gift_variant_id) {
        await HPAutoCampaignControl.addGiftToCart(gift_variant_id);
      } else if (should_remove) {
        await HPAutoCampaignControl.setGiftLines(gift_keys, false); // tümünü sil
      } else {
        await HPAutoCampaignControl.setGiftLines(gift_keys, true); // tek 1 adet bırak
      }
    }
  }

  /**
   * Sayfa yüklendiğinde bir kez api-campaign-calculate render edip kontrol sağlar.
   */
  static async runInitialCheck() {
    const campaignData = await HPAutoCampaignControl.getCampaignDataFromResponse({});
    if (!campaignData) return;

    document.dispatchEvent(
      new CustomEvent(HPAutoCampaignControl.EVENT_NAME, { detail: campaignData })
    );
  }

  /**
   * Event listener'ı kaydeder ve ilk kontrolü çalıştırır.
   */
  static init() {
    document.addEventListener(
      HPAutoCampaignControl.EVENT_NAME,
      HPAutoCampaignControl.handleCampaignCalculate
    );

    HPAutoCampaignControl.runInitialCheck().catch((err) =>
      console.warn('hp-auto-campaign-control: Initial check failed', err)
    );
  }
}

window.dispatchCampaignCalculateIfNeeded = HPAutoCampaignControl.dispatchCampaignCalculateIfNeeded.bind(
  HPAutoCampaignControl
);

// Paint-only: re-render handler'ları (cart:updated, drawer) bunu çağırır.
window.refreshCampaignGiftBar = HPAutoCampaignControl.refreshGiftBarOnly.bind(
  HPAutoCampaignControl
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HPAutoCampaignControl.init());
} else {
  HPAutoCampaignControl.init();
}
